/**
 * buildMorphUrl — Constructs the iframe src URL for the shop viewers.
 *
 * Three viewers share one scan location and one SAS acquisition:
 *   v1  /morph-viewer/morph-viewer-v2.html  — Delaunay morph, driven by cameras.json
 *   v2  /depth-viewer/index.html            — depth mesh, driven by depth-v2/scan.json
 *   v3  /cloud-viewer/index.html            — the fused scene, DepthwithColmap/cloud.json
 *
 * All take the same ?base=<section dir>&sas=<token>, so resolving the base is done once and
 * the builders are thin wrappers over it. Handles three camerasPath formats:
 *   1. Full blob URL:   https://...blob.../section_0/cameras.json
 *   2. API proxy URL:   /api/scan-data/shopId/section_0/cameras.json
 *   3. Relative path:   shopId/section_0/cameras.json
 */
import { apiUrl, authFetch } from '@/config'

export interface DatasetWithPaths {
  id: string
  paths?: {
    cameras?: string
    [key: string]: any
  }
}

const MORPH_VIEWER = '/morph-viewer/morph-viewer-v2.html'
const DEPTH_VIEWER = '/depth-viewer/index.html'
const CLOUD_VIEWER = '/cloud-viewer/index.html'

/** Where a scan's section lives, and the token needed to read it. */
interface ScanLocation {
  base: string
  sas: string
}

function viewerUrl(page: string, { base, sas }: ScanLocation): string {
  const params = new URLSearchParams(sas ? { base, sas } : { base })
  return `${page}?${params.toString()}`
}

/**
 * Resolve a cameras.json path of any of the three formats into a base + SAS.
 *
 * Never throws: every failure path degrades to something the viewer can still load, because a
 * missing token is recoverable (the blob may allow anonymous read, or the API proxy works)
 * while a rejected promise would leave the page with no iframe at all.
 */
async function resolveScanLocation(camerasPath: string): Promise<ScanLocation> {
  // ── Case 1: Full blob URL ─────────────────────────────────────────────────
  if (camerasPath.startsWith('https://') || camerasPath.startsWith('http://')) {
    const base = camerasPath.substring(0, camerasPath.lastIndexOf('/') + 1)
    try {
      // Scanner-uploaded shops live in "scan-frames"; old COLMAP shops in "scan-results"
      const isScanFrames = camerasPath.includes('/scan-frames/')
      let sas = ''
      if (isScanFrames) {
        const res = await authFetch(apiUrl('/api/scan-frames-token'))
        if (res.ok) {
          const data = await res.json()
          sas = data.sasToken ?? data.sas_token ?? ''
        }
      } else {
        const res = await authFetch(apiUrl('/api/scan-data-token'))
        if (res.ok) {
          const { sas_token } = await res.json()
          sas = sas_token ?? ''
        }
      }
      if (sas) return { base, sas }
    } catch { /* fall through */ }
    // No SAS — the blob may allow anonymous read.
    return { base, sas: '' }
  }

  // ── Case 2: API proxy URL (/api/scan-data/...) or relative path ────────────
  const apiPrefix = '/api/scan-data/'
  const blobRelative = camerasPath.startsWith(apiPrefix)
    ? camerasPath.substring(apiPrefix.length, camerasPath.lastIndexOf('/') + 1)
    : camerasPath.substring(0, camerasPath.lastIndexOf('/') + 1)

  try {
    const res = await authFetch(apiUrl('/api/scan-data-token'))
    if (res.ok) {
      const { base_url, sas_token } = await res.json()
      return { base: `${base_url}/${blobRelative}`, sas: sas_token ?? '' }
    }
  } catch { /* fall through */ }

  // Fallback: API proxy (slower but always works)
  return { base: apiUrl(`${apiPrefix}${blobRelative}`), sas: '' }
}

/**
 * Build the v1 morph viewer URL from a cameras.json path (any format).
 * Fetches SAS for direct blob access; falls back to the API proxy.
 */
export async function buildMorphUrl(dataset: DatasetWithPaths): Promise<string> {
  const camerasPath = dataset.paths?.cameras || `/api/scan-data/${dataset.id}/section_0/cameras.json`
  return viewerUrl(MORPH_VIEWER, await resolveScanLocation(camerasPath))
}

/**
 * Build the v1 morph viewer URL from a section directory.
 * Used after loadDataset sets currentSectionFramesPath for the best section.
 * E.g. base = 'https://...section_2/' (the directory containing cameras.json)
 */
export async function buildMorphUrlFromBase(base: string): Promise<string> {
  return viewerUrl(MORPH_VIEWER, await resolveScanLocation(base + 'cameras.json'))
}

/**
 * The v2 depth viewer URL for a scan already resolved for v1.
 *
 * A page swap, nothing more. Both viewers take the identical ?base=&sas=, and by the time we
 * have a morph URL the base and token are already resolved — rebuilding them would fetch a
 * second SAS for the same container and could hand the two iframes different tokens.
 *
 * Returns null when the input is not a morph URL, so a caller cannot accidentally point the
 * depth viewer at something it cannot read.
 */
export function depthUrlFrom(morphUrl: string | null): string | null {
  if (!morphUrl || !morphUrl.startsWith(MORPH_VIEWER)) return null
  return DEPTH_VIEWER + morphUrl.slice(MORPH_VIEWER.length)
}

/** The v3 scene viewer URL for a scan already resolved for v1. Same page swap as above. */
export function cloudUrlFrom(morphUrl: string | null): string | null {
  if (!morphUrl || !morphUrl.startsWith(MORPH_VIEWER)) return null
  return CLOUD_VIEWER + morphUrl.slice(MORPH_VIEWER.length)
}

/** The ?base= and ?sas= a resolved viewer URL was built with. */
export function scanLocationOf(viewerUrlString: string | null): ScanLocation | null {
  if (!viewerUrlString) return null
  const query = viewerUrlString.split('?')[1]
  if (!query) return null
  const params = new URLSearchParams(query)
  const base = params.get('base')
  return base ? { base, sas: params.get('sas') ?? '' } : null
}

/**
 * Does this section have a depth-v2 scan the v2 viewer can open?
 *
 * GET rather than HEAD: the API proxy path in this stack does not route HEAD, and the manifest
 * is ~20 KB — small enough that fetching it warms the cache for the viewer about to request it.
 */
export async function hasDepthData(location: ScanLocation): Promise<boolean> {
  const { base, sas } = location
  try {
    const res = await fetch(`${base}depth-v2/scan.json${sas ? `?${sas}` : ''}`)
    return res.ok
  } catch {
    return false
  }
}

/**
 * Does this section have a fused scene the v3 viewer can open?
 *
 * cloud.json rather than cloud.bin: the manifest is a couple of KB and the cloud is ~9 MB, and
 * a probe that downloads nine megabytes to answer yes/no would cost more than the feature.
 *
 * DepthwithColmap/ is written entirely by the scene pipeline and read by nothing else, so its
 * manifest existing is a complete answer — there is no half-built state where the folder has a
 * cloud.json but not the rest.
 */
export async function hasCloudData(location: ScanLocation): Promise<boolean> {
  const { base, sas } = location
  try {
    const res = await fetch(`${base}DepthwithColmap/cloud.json${sas ? `?${sas}` : ''}`)
    return res.ok
  } catch {
    return false
  }
}
